/* eslint-disable camelcase */
import { nanoid } from 'nanoid'
import cloneDeep from 'lodash/cloneDeep'

import { normalizeStreetWidth, recalculateWidth } from '../../streets/width'
import { DEFAULT_ENVIRONS } from '../../streets/constants'
import { getSignInData, isSignedIn } from '../../users/authentication'
import { getVariantString } from '../../segments/variant_utils'
import { getSegmentVariantInfo } from '../../segments/info'
import {
  normalizeSegmentWidth,
  RESIZE_TYPE_INITIAL,
  resolutionForResizeType
} from '../../segments/resizing'
import { STREET_SEGMENTS } from '../config/segments.config'
import {
  prepareCustomStreet,
  saveStreetToServerIfNecessary,
  setIgnoreStreetChanges,
  setLastStreet
} from '../../streets/data_model'
import { segmentsChanged } from '../../segments/view'
import { saveStreetToServer } from '../../streets/xhr'
import { DEFAULT_SEGMENTS } from '../../segments/default'
import store from '../../store'
import { updateSegments, updateStreetData } from '../../store/slices/street'
import { setShowStreetmixToaster } from '../slices/appSlice'
import { get } from 'lodash'
import { remixStreet } from '../../streets/remix'

// Original location: assets/scripts/streets/data_model.js
const DEFAULT_BUILDING_HEIGHT_LEFT = 5
const DEFAULT_BUILDING_HEIGHT_RIGHT = 5
const DEFAULT_BUILDING_VARIANT_LEFT = 'narrow'
const DEFAULT_BUILDING_VARIANT_RIGHT = 'wide'
const DEFAULT_STREET_WIDTH = 80
const LATEST_SCHEMA_VERSION = 27

export function makeCustomStreet(landUseLabel) {
  setIgnoreStreetChanges(true)
  prepareCustomStreet(landUseLabel)

  segmentsChanged()

  setIgnoreStreetChanges(false)
  setLastStreet()

  // saveStreetToServer(false)
}

function fillCustomSegments(units, currentStreet) {
  const segments = []

  for (const i in currentStreet) {
    const segment = cloneDeep(currentStreet[i])

    segment.variantString = getVariantString(segment.variant)

    const variantInfo = getSegmentVariantInfo(
      segment.type,
      segment.variantString
    )

    segment.id = nanoid()
    segment.width = normalizeSegmentWidth(
      segment.width,
      resolutionForResizeType(RESIZE_TYPE_INITIAL, units)
    )
    segment.elevation = variantInfo.elevation
    segment.warnings = []

    segments.push(segment)
  }

  return segments
}

export function getStreetConfig(landUseLabel) {
  const units = 1 // Unit of measurement (1 = imperial, 2 = metric)
  const currentDate = new Date().toISOString()
  const { street } = store.getState()

  const currentStreet = cloneDeep(STREET_SEGMENTS[landUseLabel])

  const streetConfig = {
    units,
    location: null,
    name: street.name || null,
    showAnalytics: true,
    userUpdated: false,
    editCount: 0,
    width: recalculateWidth(currentStreet).occupiedWidth,

    // occupiedWidth: recalculateWidth(currentStreet).occupiedWidth,
    // remainingWidth: recalculateWidth(currentStreet).remainingWidth,
    environment: DEFAULT_ENVIRONS,
    leftBuildingHeight:
      currentStreet?.DEFAULT_BUILDING_HEIGHT_LEFT ||
      DEFAULT_BUILDING_HEIGHT_LEFT,
    leftBuildingVariant:
      currentStreet?.DEFAULT_BUILDING_VARIANT_LEFT ||
      DEFAULT_BUILDING_VARIANT_LEFT,
    rightBuildingHeight:
      currentStreet?.DEFAULT_BUILDING_HEIGHT_RIGHT ||
      DEFAULT_BUILDING_HEIGHT_RIGHT,
    rightBuildingVariant:
      currentStreet?.DEFAULT_BUILDING_VARIANT_RIGHT ||
      DEFAULT_BUILDING_VARIANT_RIGHT,
    schemaVersion: LATEST_SCHEMA_VERSION,
    segments: currentStreet?.segments
      ? fillCustomSegments(units, currentStreet.segments)
      : fillCustomSegments(units, DEFAULT_SEGMENTS.false),
    updatedAt: currentDate,
    clientUpdatedAt: currentDate,

    creatorId: (isSignedIn() && getSignInData().userId) || null
  }

  return streetConfig
}

export function updateStreetBasedOnTravelLane(landUseLabel) {
  const units = 1 // Unit of measurement (1 = imperial, 2 = metric)
  const currentDate = new Date().toISOString()
  const { street, roadwayVariables } = store.getState()
  const { travel_lanes, street_width } = roadwayVariables
  const currentStreet = cloneDeep(STREET_SEGMENTS[landUseLabel])
  const driveLaneInbound = {
    type: 'drive-lane',
    width: 8,
    variantString: 'inbound|car',
    variant: {
      direction: 'inbound',
      'car-type': 'car'
    },
    elevation: 0
  }
  const driveLaneOutbound = {
    type: 'drive-lane',
    width: 8,
    variantString: 'outbound|car',
    variant: {
      direction: 'outbound',
      'car-type': 'car'
    },
    elevation: 0
  }
  const totalTravelLanesInCurrentSt = currentStreet.segments.filter((segment) =>
    ['drive-lane', 'bus-lane', 'brt-lane'].includes(segment.type)
  ).length

  if (travel_lanes < totalTravelLanesInCurrentSt) {
    const travelLanesToBeRemoved = Math.abs(
      travel_lanes - totalTravelLanesInCurrentSt
    )
    const segmentsToBeRemoved = currentStreet.segments
      .filter((segment) =>
        ['drive-lane', 'bus-lane', 'brt-lane'].includes(segment.type)
      )
      .slice(-travelLanesToBeRemoved)
      .map((segment) => segment.id)

    currentStreet.segments = currentStreet.segments.filter(
      (segment) => !segmentsToBeRemoved.includes(segment.id)
    )
  } else {
    const travelLanesToBeAdded = Math.abs(
      travel_lanes - totalTravelLanesInCurrentSt
    )
    const inBoundLanesToBeAdded =
      travelLanesToBeAdded % 2 === 0
        ? travelLanesToBeAdded / 2
        : Math.floor(travelLanesToBeAdded / 2) + 1
    const outBoundLanesToBeAdded = travelLanesToBeAdded - inBoundLanesToBeAdded

    const inboundLanes = Array(inBoundLanesToBeAdded).fill(driveLaneInbound)
    const outboundLanes = Array(outBoundLanesToBeAdded).fill(driveLaneOutbound)

    const firstLaneIndex = currentStreet.segments.findIndex((segment) =>
      ['drive-lane', 'bus-lane', 'brt-lane'].includes(segment?.type)
    )

    currentStreet.segments = addMultipleSegments(
      inboundLanes,
      firstLaneIndex,
      currentStreet.segments
    )

    // Reverse segments to find last lane id
    const copyOfSegmentsToReverse = [...currentStreet.segments]
    const lastLaneId = copyOfSegmentsToReverse
      .reverse()
      .find((segment) =>
        ['drive-lane', 'bus-lane', 'brt-lane'].includes(segment?.type)
      ).id

    const lastLandeIndex = currentStreet.segments.findIndex(
      (segment) => segment?.id === lastLaneId
    )

    currentStreet.segments = addMultipleSegments(
      outboundLanes,
      lastLandeIndex + 1,
      currentStreet.segments
    )
  }

  const streetConfig = {
    units,
    location: null,
    name: street.name || null,
    showAnalytics: true,
    userUpdated: false,
    editCount: 0,
    width: street_width,

    occupiedWidth: recalculateWidth(currentStreet).occupiedWidth,
    remainingWidth: recalculateWidth(currentStreet).remainingWidth,
    environment: DEFAULT_ENVIRONS,
    leftBuildingHeight:
      currentStreet?.DEFAULT_BUILDING_HEIGHT_LEFT ||
      DEFAULT_BUILDING_HEIGHT_LEFT,
    leftBuildingVariant:
      currentStreet?.DEFAULT_BUILDING_VARIANT_LEFT ||
      DEFAULT_BUILDING_VARIANT_LEFT,
    rightBuildingHeight:
      currentStreet?.DEFAULT_BUILDING_HEIGHT_RIGHT ||
      DEFAULT_BUILDING_HEIGHT_RIGHT,
    rightBuildingVariant:
      currentStreet?.DEFAULT_BUILDING_VARIANT_RIGHT ||
      DEFAULT_BUILDING_VARIANT_RIGHT,
    schemaVersion: LATEST_SCHEMA_VERSION,
    segments: currentStreet?.segments
      ? fillCustomSegments(units, currentStreet.segments)
      : fillCustomSegments(units, DEFAULT_SEGMENTS.false),
    updatedAt: currentDate,
    clientUpdatedAt: currentDate,

    creatorId: (isSignedIn() && getSignInData().userId) || null
  }

  // remixStreet()
  store.dispatch(updateStreetData(streetConfig))
}

export function updateStreetWidth(width) {
  const { street } = store.getState()

  const recalcualtedOccupiedWidth = recalculateWidth({
    ...street
  }).occupiedWidth

  const modifiedStreet = {
    ...street,
    width: Number(width),
    occupiedWidth: recalcualtedOccupiedWidth,
    remainingWidth: Number(width) - recalcualtedOccupiedWidth
  }

  store.dispatch(updateStreetData(modifiedStreet))
  // store.dispatch(
  //   updateSegments(
  //     updatedStreet.segments,
  //     updatedStreet.occupiedWidth,
  //     updatedStreet.remainingWidth
  //   )
  // )

  setIgnoreStreetChanges(false)

  // saveStreetToServer(false)
}

export function updateFinalStreet(segmentCopy) {
  const { street, roadwayVariables } = store.getState()
  const { street_width } = roadwayVariables
  const recalcualtedOccupiedWidth = recalculateWidth({
    ...street,
    segments: segmentCopy
  }).occupiedWidth

  const modifiedStreet = {
    ...street,
    width: street_width,
    occupiedWidth: recalcualtedOccupiedWidth,
    remainingWidth: street_width - recalcualtedOccupiedWidth,
    segments: segmentCopy
  }

  store.dispatch(updateStreetData(modifiedStreet))
  // const updatedStreet = recalculateWidth(modifiedStreet)
  // store.dispatch(
  //   updateSegments(updatedStreet.segments, updatedStreet.occupiedWidth, 0)
  // )

  setIgnoreStreetChanges(false)

  // saveStreetToServer(false)
  saveStreetToServerIfNecessary()
  // Once street has been updated show toaster
  store.dispatch(setShowStreetmixToaster(true))
}

export function addMultipleSegments(
  toBeAddedSegments,
  insertIndex,
  segmentCopy
) {
  segmentCopy.splice(insertIndex, 0, ...toBeAddedSegments)

  return segmentCopy
}

export function addSegment(index, segment, segmentCopy) {
  segmentCopy.splice(index, 0, segment)
  return segmentCopy
}

export function addSharedLaneSegment(index, segment, segmentCopy) {
  segmentCopy.splice(index, 1, segment)
  return segmentCopy
}

export function removeAllMatchingSegments(segmentCopy, segmentType) {
  // get all macthing segments indexes
  const segmentIndexArr = segmentCopy
    .map((segment, index) => {
      if (segment.type === segmentType) {
        return index
      } else {
        return null
      }
    })
    .filter((index) => index !== null)
  return segmentCopy.filter(
    (segment, index) => !segmentIndexArr.includes(index)
  )
}

export function removeAllMatchingSegmentsWithVariant(
  segmentCopy,
  segmentType,
  segmentVariant
) {
  // get all macthing segments indexes
  const sidewalkIndexArr = segmentCopy
    .map((segment, index) => {
      if (
        segment.type === segmentType &&
        segment.variantString === segmentVariant
      ) {
        return index
      } else {
        return null
      }
    })
    .filter((index) => index !== null)
  return segmentCopy.filter(
    (segment, index) => !sidewalkIndexArr.includes(index)
  )
}

export function removeAllSimilarSegmentNames(segmentCopy, segmentType) {
  // get all macthing segments indexes
  const sidewalkIndexArr = segmentCopy
    .map((segment, index) => {
      if (segment.type.includes(segmentType)) {
        return index
      } else {
        return null
      }
    })
    .filter((index) => index !== null)
  return segmentCopy.filter(
    (segment, index) => !sidewalkIndexArr.includes(index)
  )
}
